import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import { NODE_API_HOST, SERVER_API_HOST } from '../config';
import { loggerEvent } from '../logger';
import { getClientStore } from '../state';
import type { IAuthData } from '../types';
import type { TEnvironment } from '../utils/env';
import { getBrowserEnv, getNodeEnv, isBrowser, isProd } from '../utils/env';
import { EMIT_EVENT, eventBus } from '../utils';
import { getCommonParams } from './params';
import Request from './request';
import { signCalc } from './sign';

let env = '';
if (isBrowser) {
  env = getBrowserEnv();
} else {
  env = getNodeEnv();
}

const getLane = (config: AxiosRequestConfig) => {
  if (isBrowser) {
    return config.headers?.['lane'];
  } else {
    return process.env.SWIM_LANE; // 本地.env.development/bedrock环境变量
  }
};

const HTTP_STATUS_CODE = {
  SUCCESS: [200, 201, 204],
  NEED_REFRESH_TOKEN: 1101010121,
  REFRESH_TOKEN_FAIL: 1101010111,
};

const defaultConfig: AxiosRequestConfig = {
  // 服务端超时限制在 2s 内，客户端可以放宽到 5min
  timeout: isBrowser ? 60 * 5 * 1000 : 2 * 1000,
  headers: {
    'Content-type': 'application/json',
  },
};

const request = new Request('', defaultConfig);

const requestInterceptor = (config: AxiosRequestConfig) => {
  const { url } = config;
  if (isBrowser) {
    config.baseURL = '';
  } else if (url?.startsWith('/node')) {
    config.baseURL = NODE_API_HOST[env as TEnvironment];
  } else {
    config.baseURL = SERVER_API_HOST[env as TEnvironment];
  }

  let token = '';
  if (isBrowser) {
    const authData = getClientStore().getState().user.authData;
    token = authData?.authToken || '';
  } else {
    // server 获取参数里的 authData
    const authData = config.params?.authData as IAuthData;
    token = authData?.authToken || '';
  }
  const params = {
    ...getCommonParams(config.params?.authData),
    ...config.params,
  };
  delete params?.authData;

  const [sign, timestamp] = signCalc(params, config.data);

  const headers = {
    'x-token': token,
    'x-timestamp': timestamp,
    'x-sign': sign,
    lane: getLane(config),
    ...config.headers,
  };

  if (isProd) {
    delete (headers as any)['lane'];
  }

  return {
    ...config,
    headers,
    params,
  } as any;
};

const responseInterceptor = (response: AxiosResponse) => {
  const { status, data } = response;

  !isBrowser &&
    loggerEvent({
      type: 'api_access',
      message: 'Api Request Completed',
      res: response,
    });
  if (
    !HTTP_STATUS_CODE.SUCCESS.includes(status) ||
    (data && data.base_resp && data.base_resp.status_code !== 0) ||
    (data && typeof data.status_code === 'number' && data.status_code !== 0) // 兼容部分接口没有base_resp的情况
  ) {
    const base_resp = data.base_resp || {
      status_code: data.status_code,
      status_msg: data.status_msg,
    };
    throw base_resp;
  }

  return response;
};

const commonErrorHander = (error: AxiosError) => {
  const response: AxiosResponse = error.response as AxiosResponse;
  const data = response?.data;

  !isBrowser &&
    loggerEvent({
      type: 'api_error',
      message: 'Api Request Error',
      res: error as any,
      isError: true,
      extra: {
        responseHeaders: response?.headers,
      },
    });

  if (
    (data && data.base_resp && data.base_resp.status_code !== 0) ||
    (data && typeof data.status_code === 'number' && data.status_code !== 0)
  ) {
    const base_resp = data.base_resp || {
      status_code: data.status_code,
      status_msg: data.status_msg,
    };
    // auth_token 或 refresh_token 过期，让前端弹兜底文案+登录弹窗
    if (
      isBrowser &&
      (base_resp.status_code === HTTP_STATUS_CODE.NEED_REFRESH_TOKEN ||
        base_resp.status_code === HTTP_STATUS_CODE.REFRESH_TOKEN_FAIL)
    ) {
      eventBus.emit(EMIT_EVENT.TOKEN_EXPIRED, base_resp);
    }
    throw base_resp;
  } else if (data) {
    throw data;
  } else {
    throw error;
  }
};

request.addRequestInterceptor(requestInterceptor);

// response interceptor
request.addResponseInterceptor(responseInterceptor, commonErrorHander);

export { request };
